import { Anchor, AnchorGroup } from "@/components/anchors-navigation";
import CustomArrowShape from "@/components/CustomArrowShape";
import FilterButton from "@/components/filtering/Button";
import UserComments from "@/components/UserComments";
import { useXarrowMediaObservers } from "@/hooks";
import { PageType } from "@/services/shared-models";
import { ROOT_NODE, useEditor, useNode } from "@craftjs/core";
import clsx from "clsx";
import { Children, isValidElement, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Xarrow from "react-xarrows";
import TableOfContents from "./components/TableOfContents";
import Settings from "./Settings";

interface PageContentProps {
    filters?: string;
    toc?: string;
    tocParent?: string;
    tags?: string;
    anchors?: string;
    children?: React.ReactNode;
}

interface AnchorData {
    id: string;
    text: string;
    tag?: string;
}

const PageContent = ({ filters = "yes", toc = "no", tags = "yes", anchors = "no", children }: PageContentProps) => {
    const [anchorData, setAnchors] = useState<AnchorData[]>([]);
    const [leads, setLeads] = useState<boolean>(false);
    const [spotlightLeads, setSpotlightLeads] = useState<boolean>(false);

    const {
        connectors: { connect },
    } = useNode();

    const columnCount = useMemo(
        () => 1 + (filters === "yes" || anchors === "yes" || toc === "yes" ? 1 : 0) + (tags === "yes" ? 1 : 0),
        [filters, anchors, toc, tags]
    );

    const { enabled, nodes, comments } = useEditor((state) => ({
        enabled: state.options.enabled,
        nodes: state.nodes,
        comments: state.nodes[ROOT_NODE].data.props.comments === "yes",
    }));

    const { t } = useTranslation("common");

    const containerRef = useRef<HTMLDivElement | null>(null);
    useXarrowMediaObservers(containerRef.current, [
        "lead-start",
        "lead-end",
        "lead-spotlight-start",
        "lead-spotlight-end",
    ]);

    useEffect(() => {
        if (anchors === "no") {
            setAnchors([]);
            return;
        }

        setTimeout(() => {
            const headings = document.querySelectorAll(".craftjs-heading-anchor");
            const data: AnchorData[] = [];

            headings.forEach((heading, index) => {
                const id = "block-" + (index + 1);
                const text = heading.getAttribute("data-anchor-label") || "";
                const tag = heading.getAttribute("data-anchor-tag") || "";

                if (text) {
                    heading.id = id; // Set the ID for the heading element
                    data.push({ id, text, tag });
                }
            });

            if (comments) {
                data.push({
                    id: "block-comments",
                    text: t("comments.title"),
                });
            }

            setAnchors(data);
        }, 100);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anchors, comments, nodes, children]);

    useEffect(() => {
        if (tags !== "yes") return;

        const leadElements = document.querySelectorAll(".lead");
        if (leadElements.length === 0) {
            return;
        }

        const firstLead = leadElements[0];
        const leadStart = document.querySelector("#lead-start");
        if (!leadStart) {
            const leadStartTemplate = document.querySelector("#lead-0");
            if (leadStartTemplate) {
                const newLeadStart = leadStartTemplate.cloneNode(true) as HTMLSpanElement;
                newLeadStart.id = "lead-start";
                firstLead.parentNode?.insertBefore(newLeadStart, firstLead);
            }
        } else {
            firstLead.parentElement?.insertBefore(leadStart, firstLead);
        }

        const lastLead = leadElements[leadElements.length - 1];
        const leadEnd = document.querySelector("#lead-end");
        if (!leadEnd) {
            const leadEndTemplate = document.querySelector("#lead-99");
            if (leadEndTemplate) {
                const newLeadEnd = leadEndTemplate.cloneNode(true) as HTMLSpanElement;
                newLeadEnd.id = "lead-end";
                lastLead.parentNode?.insertBefore(newLeadEnd, lastLead);
            }
        } else {
            lastLead.parentElement?.insertBefore(leadEnd, lastLead);
        }

        setLeads(true);

        const spotLightLead = document.querySelector("#lead-spotlight-end");
        if (spotLightLead) setSpotlightLeads(true);
    }, [columnCount, tags, filters, children]);

    // Calculate the amount of children within the children prop
    const rowCount = (
        Children.map(children ?? [], (child) => {
            if (isValidElement(child)) {
                return child.props.children.length;
            }

            return 0;
        }) ?? []
    ).reduce((acc, curr) => acc + curr, 0);

    return (
        <div ref={(ref) => connect(ref!)}>
            <main ref={containerRef} className="container relative mx-auto 2xl:px-[108px] z-[1] pb-[160px]">
                {leads && (
                    <Xarrow
                        start="lead-start"
                        end="lead-end"
                        color="#8dd6d0"
                        dashness={{ strokeLen: 15, nonStrokeLen: 15 }}
                        strokeWidth={5}
                        startAnchor={"auto"}
                        endAnchor={"top"}
                        headSize={0}
                        showHead={false}
                        path="straight"
                    />
                )}

                <div
                    style={{ gridTemplateRows: `repeat(${rowCount}, auto)` }}
                    className={clsx([
                        "relative grid",
                        columnCount === 1 && "grid-cols-[1fr]",
                        columnCount === 2 &&
                            (filters === "yes" || anchors === "yes" || toc === "yes") &&
                            "grid-cols-[auto_1fr]",
                        columnCount === 2 && tags === "yes" && "grid-cols-[64px_1fr]",
                        columnCount === 3 && "grid-cols-[auto_64px_1fr]",
                        "gap-13 gap-y-50 mt-36",
                    ])}
                >
                    {(filters === "yes" || anchors === "yes" || toc === "yes") && (
                        <AnchorGroup>
                            {filters === "yes" && <FilterButton />}
                            {anchors === "yes" &&
                                anchorData.map((anchor) => (
                                    <Anchor key={anchor.id} id={anchor.id} tag={anchor.tag} small>
                                        {anchor.text}
                                    </Anchor>
                                ))}
                            {toc === "yes" && <TableOfContents />}
                        </AnchorGroup>
                    )}

                    {children}

                    {comments && (
                        <section
                            id="block-comments"
                            className={clsx([columnCount === 2 && "col-start-2", columnCount === 3 && "col-start-3"])}
                        >
                            {enabled ? (
                                <>
                                    <div className="flex items-center justify-between">
                                        <h2 className="font-bold text-[45px] text-primary-500">
                                            {t("comments.title")}
                                        </h2>
                                    </div>
                                    <div className="flex h-20 items-center italic">
                                        Comments will be displayed here on the live page.
                                    </div>
                                </>
                            ) : (
                                <UserComments pageType={PageType.Content} />
                            )}
                        </section>
                    )}
                </div>

                <div id="lead-spotlight-start" className="col-span-2 w-[1px] mx-auto items-center" />

                {spotlightLeads && (
                    <Xarrow
                        start="lead-spotlight-start"
                        end="lead-spotlight-end"
                        color="#8dd6d0"
                        dashness={{ strokeLen: 15, nonStrokeLen: 15 }}
                        strokeWidth={5}
                        startAnchor={"top"}
                        endAnchor={"top"}
                        showHead={true}
                        headShape={CustomArrowShape}
                        headSize={1}
                        showTail={true}
                        tailShape={"circle"}
                        tailSize={5}
                        path="straight"
                        curveness={0.95}
                    />
                )}
            </main>

            <span id="lead-0" className="absolute -top-36 w-16 h-1" />
            <span id="lead-99" className="absolute top-0 w-16 h-1" />
        </div>
    );
};

PageContent.craft = {
    displayName: "Content",
    props: {
        filters: "no",
        tags: "no",
        anchors: "no",
        toc: "no",
        tocParent: undefined,
    },
    related: {
        settings: Settings,
    },
    rules: {
        canDrag: () => false,
        canMoveOut: () => false,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        canDrop: (_: any, currentNode: any) => currentNode.data.name === "PageContentContainer",
    },
    custom: {
        canDelete: false,
    },
};

export default PageContent;
